import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Handler } from 'leaflet';
import _ from 'lodash';

declare global {
    interface Date {
        isLeapYear: () => any;
        getDaysInMonth: () => any;
        addMonths: (value) => any;
    }

}

Date.prototype.isLeapYear = function() {
    const year = this.getFullYear();
    return ((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0);
};

Date.prototype.getDaysInMonth = function() {
    const year = this.getFullYear();
    const month = this.getMonth();
    return [31, (this.isLeapYear() ? 29 : 28), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
};

Date.prototype.addMonths = function(value) {
    const date = this.getDate();
    this.setDate(1);
    this.setMonth(this.getMonth() + value);
    this.setDate(Math.min(date, this.getDaysInMonth()));
    return this;
};

@Injectable()
export class Utils {

    static isSameDateAs(date1: Date, date2: Date) {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    }

    static addDays(date: Date, days): Date {
        const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(),
            date.getMinutes(), date.getSeconds(), date.getMilliseconds());
        newDate.setDate(newDate.getDate() + days);
        return newDate;
    }

    static orderBy(data: any, property: string, dir: string) {
        return _.orderBy(data, [property], [dir]);
    }

    static orderByList(data: any, property: any, dir: any) {
        return _.orderBy(data, property, dir);
    }



    static padNumber(no) {
        return no > 9 ? '' + no : '0' + no;
    }

    static calculateDistance(lat1: number, long1: number, lat2: number, long2: number): number {

        const r = 6371;

        const dLat: number = this.toRadian((lat2 - lat1));

        const dLon: number = this.toRadian((long2 - long1));

        lat1 = this.toRadian(lat1);

        lat2 = this.toRadian(lat2);

        const a: number = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLon / 2) *
            Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);

        const c: number = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        const d: number = r * c;

        return d;

    }

    static toRadian(value: number): number {
        return value * Math.PI / 180;
    }

    static RoundNumber(value:number)
    {
        return Math.round(value);
    }

    /**
     * Validates that GPS coordinates are within valid ranges.
     * Latitude: -90 to 90, Longitude: -180 to 180.
     * Returns false for 0,0 and for values that look like non-decimal Norwegian format (e.g. 5798794 instead of 57.98794).
     */
    static isValidGpsCoordinate(lat: number, lng: number): boolean {
        if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
            return false;
        }
        if (lat === 0 && lng === 0) {
            return false;
        }
        return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    }

    /**
     * Attempts to sanitize a GPS coordinate value that may have been corrupted
     * by Norwegian locale (comma as decimal separator causing e.g. 5798794 instead of 57.98794).
     * If the value is outside valid range but can be fixed by inserting a decimal point, returns the fixed value.
     * Otherwise returns the original value.
     */
    static sanitizeGpsValue(value: number, isLatitude: boolean): number {
        if (value == null || isNaN(value)) return 0;
        
        const max = isLatitude ? 90 : 180;
        const min = isLatitude ? -90 : -180;
        
        // Already valid
        if (value >= min && value <= max) return value;
        
        // Try to fix by inserting decimal point at various positions
        const absValue = Math.abs(value);
        const sign = value < 0 ? -1 : 1;
        const str = absValue.toString().replace('.', '');
        
        // Try inserting decimal after position 1, 2, 3
        for (let i = 1; i <= Math.min(3, str.length - 1); i++) {
            const candidate = parseFloat(str.substring(0, i) + '.' + str.substring(i)) * sign;
            if (candidate >= min && candidate <= max && candidate !== 0) {
                console.warn(`GPS sanitize: ${value} -> ${candidate} (${isLatitude ? 'lat' : 'lng'})`);
                return candidate;
            }
        }
        
        // Could not fix — return 0 to signal invalid
        console.warn(`GPS sanitize: could not fix ${value} (${isLatitude ? 'lat' : 'lng'}), returning 0`);
        return 0;
    }

    static async presentAlert(
        header: string,
        message: string,
        cancelButtonName: string,
        confirmButtonName: string,
        context: object,
        confirmAction: (a,b,c,d)=> {},
        cancelAction:(a,b,c)=>{},
        arg:any[]
      ) {
        
        let alertController:AlertController = new AlertController();
        const alert = await alertController.create({
          header,
          message,
          buttons: [
            {
              text: cancelButtonName,
              role: 'cancel',
              handler:cancelAction.bind(context, arg[0], arg[2],arg[3])
            }, {
              text: confirmButtonName,
              handler: confirmAction.bind(context, arg[0],arg[1],arg[2],arg[3])
            }
          ]
        });
        await alert.present();
    }

     static async presentAlertYesNo(
        header: string,
        message: string,
        cancelButtonName: string,
        confirmButtonName: string
      ) {
        let alertController:AlertController = new AlertController();
        const alert = await alertController.create({
          header,
          message,
          buttons: [
            {
              text: cancelButtonName,
              role: 'cancel',
              handler: () => {
                    alert.dismiss(false);
                    return false;
                }
            }, {
              text: confirmButtonName,
              role:'ok',
              handler: () => {
                    alert.dismiss(false);
                    return true;
                }
            }
          ]
        });
        let retValue = false;
        await alert.present();
        await alert.onDidDismiss().then((data) => {
        retValue = data.role == 'cancel'?false:true;
    })
    return retValue;
    }

}
